import { Input as ChakraInput, InputProps } from '@chakra-ui/input'
import { Box, InputGroup, BoxProps } from '@chakra-ui/react'
import React, {
  useMemo,
  forwardRef,
  useCallback,
  useState,
  ReactElement,
} from 'react'
import { useColor } from '../../../theme'
import { ErrorMessage } from '../ErrorMessage'
import { IFormValidationProps } from '../hooks'
import { useAuthLabelStyle } from '../useAuthLabelStyle'

export interface IAuthInputProps {
  value: number | string
  containerProps?: BoxProps
  rightElement?: ReactElement
}

export const AuthInput = forwardRef<
  HTMLInputElement,
  IAuthInputProps & IFormValidationProps & InputProps
>(
  (
    {
      isInvalid,
      Label,
      errorMessage,
      isDisabled,
      placeholder,
      value,
      containerProps,
      rightElement,
      ...rest
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false)

    const invalidColor = useColor({ nuance: 'red', palette: 'colors' })

    const authLabelStyle = useAuthLabelStyle()

    const onFocus = useCallback(() => setIsFocused(true), [])
    const onBlur = useCallback(() => setIsFocused(false), [])

    const showLabel = useMemo(() => !!value || isFocused, [value, isFocused])

    return (
      <Box
        data-testid="auth-input-container"
        display="grid"
        onBlur={onBlur}
        position="relative"
        {...containerProps}
      >
        {Label && showLabel && <Label {...authLabelStyle} />}

        <InputGroup>
          <ChakraInput
            data-testid="auth-input"
            errorBorderColor={invalidColor}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            onFocus={onFocus}
            placeholder={placeholder}
            ref={ref}
            variant="auth"
            {...rest}
          />

          {rightElement ? rightElement : ''}
        </InputGroup>

        {isInvalid && errorMessage && (
          <ErrorMessage isDisabled={isDisabled}>{errorMessage}</ErrorMessage>
        )}
      </Box>
    )
  },
)
